import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";

/*
  Trade relations graph (simple layout; no swimlanes)

  - Clear node labels:
      Item: Item <id> | Buying <grade> | Crop <crop> | Origin <origin>
      PO/SO: <type> <id> | Company <name> | Stakeholder <name> | SAP <no> (+ QTY on a new line)
      SR: SR <id> | Type <type>
      SI: SI <id> | No <final no> | ATA <dd MMM YYYY>

  - Edge labels with explicit prefixes:
      item->po   : PO QTY <... cases, ... KG>
      item->so   : SO QTY <... cases, ... KG>
      po/so->sr  : SR QTY <... cases, ... KG>
      sr->si     : SI QTY <... cases, ... KG>
    Both cases and KG are shown when available. Units are converted to KG when needed.
    When no volume is present but cases+netWeightPerCase exist, volume is derived.

  - Robustness:
      * Broader ID fallbacks so nodes don’t disappear.
      * Numeric parser accepts "1,234.56" and preserves 0.
      * Safe SVG export using visible bbox and white background.
      * First-open glitch avoided: we wait for the dialog/container to layout and
        also render after the enter transition completes.
*/

const PASSIVE_CODES = new Set(["P"]);

// ---------- status helpers ----------
function statusCode(row) {
  const s =
    row?.shippingInstructionRecordStatus ??
    row?.shipmentRequestRecordStatus ??
    row?.orderRecordStatus ??
    "";
  return String(s).trim().charAt(0).toUpperCase();
}
function isPassive(row) {
  return PASSIVE_CODES.has(statusCode(row));
}

// ---------- IDs (with wider fallbacks) ----------
function buildIds(row) {
  const itemId =
    row?.shippingInstructionShipmentRequestPurchaseOrderOfferItemId ??
    row?.shippingInstructionShipmentRequestSalesOrderOfferItemId ??
    row?.shipmentRequestPurchaseOrderOfferItemId ??
    row?.shipmentRequestSalesOrderOfferItemId ??
    row?.orderOfferItemId ??
    row?.purchaseOrderItemId ??
    row?.salesOrderItemId ??
    row?.offerItemId ??
    row?.itemId ??
    null;

  const poId =
    row?.shippingInstructionShipmentRequestPurchaseOrderId ??
    row?.shipmentRequestPurchaseOrderId ??
    ((row?.operationType === "PO" || row?.operationType === "PO CANCELLATION") &&
      row?.orderId) ??
    row?.purchaseOrderId ??
    null;

  const soId =
    row?.shippingInstructionShipmentRequestSalesOrderId ??
    row?.shipmentRequestSalesOrderId ??
    ((row?.operationType === "SO" || row?.operationType === "SO CANCELLATION") &&
      row?.orderId) ??
    row?.salesOrderId ??
    null;

  const srId = row?.shippingInstructionShipmentRequestId ?? row?.shipmentRequestId ?? null;
  const siId = row?.shippingInstructionId ?? null;

  return { itemId, poId, soId, srId, siId };
}

// ---------- number & unit helpers ----------
const val = (v) => {
  if (v === undefined || v === null || v === "") return null;
  if (typeof v === "string") {
    const cleaned = v.replace(/,/g, " ").replace(/\s+/g, "").trim();
    if (!cleaned) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = (v) => (v === undefined || v === null ? null : String(v));
const pick = (...xs) => xs.find((x) => x !== undefined && x !== null);

// Sanitize text for Mermaid node labels (allow <br/>)
function sanitizeHtmlLabel(label) {
  const BR = "::BR::";
  let s = String(label ?? "");
  // normalize <br> variants and newlines
  s = s.replace(/<br\s*\/?>/gi, BR).replace(/\r?\n/g, BR);
  // escape HTML entities and quotes
  s = s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  // restore <br/>
  s = s.replace(new RegExp(BR, "g"), "<br/>");
  return s;
}

// Edge labels are placed between pipes |label|; strip pipes/newlines and escape risky chars
function sanitizeEdgeLabel(label) {
  let s = String(label ?? "");
  s = s.replace(/\|/g, "/");
  s = s.replace(/\r?\n/g, " ");
  s = s.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return s;
}

const UNIT_TO_KG = {
  KG: 1,
  KGS: 1,
  KILOGRAM: 1,
  KILOGRAMS: 1,
  G: 0.001,
  GR: 0.001,
  GRAM: 0.001,
  GRAMS: 0.001,
  LB: 0.45359237,
  LBS: 0.45359237,
  POUND: 0.45359237,
  POUNDS: 0.45359237,
  MT: 1000,
  T: 1000,
  TON: 1000,
  TONS: 1000,
  TONNE: 1000,
  TONNES: 1000,
};
function toKG(volume, unit) {
  if (volume == null) return null;
  const f = UNIT_TO_KG[String(unit || "KG").trim().toUpperCase()] ?? 1;
  return volume * f;
}

function nf(n) {
  const num = Number(n);
  if (!isFinite(num)) return "";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}
function nfi(n) {
  const num = Number(n);
  if (!isFinite(num)) return "";
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(num);
}

function fmtDate(v) {
  if (v == null || v === "") return "";
  const s = String(v);

  // If ISO-like (YYYY-MM-DD...), parse parts directly to avoid timezone shifts
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, mm, dd] = iso;
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${dd} ${months[Number(mm) - 1]} ${y}`;
  }

  // Fallback: parse with Date and format using UTC to keep the day stable
  const d = new Date(v);
  if (isNaN(d.getTime())) return s;
  const day = String(d.getUTCDate()).padStart(2, "0");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const mon = months[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  return `${day} ${mon} ${year}`;
}

// ---------- flows ----------
function orderFlow(row) {
  const cases = val(pick(row?.orderNumberOfCases, row?.numberOfCases));
  let volume = val(pick(row?.orderVolume, row?.volume));
  let volumeUnit = str(pick(row?.orderVolumeUnit, row?.volumeUnit));
  if ((volume == null || !volumeUnit) && cases != null) {
    const perCase = val(
      pick(row?.orderNetWeightPerCase, row?.caseNetWeight, row?.netWeightPerCase),
    );
    if (perCase != null) {
      volume = cases * perCase;
      volumeUnit = volumeUnit || "KG";
    }
  }
  return { cases, volume, volumeUnit: volumeUnit ? volumeUnit.toUpperCase() : volumeUnit };
}
function srFlow(row) {
  const cases = val(pick(row?.shipmentRequestNumberOfCases, row?.numberOfCases));
  let volume = val(row?.shipmentRequestShipmentQuantity);
  let volumeUnit = str(pick(row?.shipmentRequestShipmentQuantityUnit, row?.volumeUnit));
  if (volume == null && cases != null) {
    const perCase = val(pick(row?.shipmentRequestNetWeightPerCase, row?.netWeightPerCase));
    if (perCase != null) {
      volume = cases * perCase;
      volumeUnit = volumeUnit || "KG";
    }
  }
  return { cases, volume, volumeUnit: volumeUnit ? volumeUnit.toUpperCase() : volumeUnit };
}
function siFlow(row) {
  const cases = val(row?.shippingInstructionShipmentRequestNumberOfCases);
  let volume = val(row?.shippingInstructionShipmentRequestShipmentQuantity);
  let volumeUnit = str(row?.shippingInstructionShipmentRequestShipmentQuantityUnit);
  if (volume == null && cases != null) {
    const perCase = val(row?.shippingInstructionShipmentRequestNetWeightPerCase);
    if (perCase != null) {
      volume = cases * perCase;
      volumeUnit = volumeUnit || "KG";
    }
  }
  return { cases, volume, volumeUnit: volumeUnit ? volumeUnit.toUpperCase() : volumeUnit };
}

function nodeKey(type, id) {
  return `${type}-${id}`;
}
function sanitizeId(id) {
  return (id || "").replace(/[^a-zA-Z0-9_:-]/g, "_");
}
function numPart(id) {
  const m = String(id).match(/-(\d+)$/);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}
function shapeWrap(type, label) {
  const safe = sanitizeHtmlLabel(label);
  if (type === "item") return `["${safe}"]`;
  if (type === "po") return `("${safe}")`;
  if (type === "so") return `(["${safe}"])`;
  if (type === "sr") return `{"${safe}"}`;
  if (type === "si") return `[["${safe}"]]`;
  return `["${safe}"]`;
}

// ---------- labels (built fresh; no accumulation) ----------
function itemLabel(itemId, row) {
  const parts = [`Item ${itemId}`];
  const buying = pick(
    row?.orderOfferItemBuyingGrade,
    row?.itemBuyingGrade,
    row?.salesOrderItemBuyingGrade,
    row?.purchaseOrderItemBuyingGrade,
  );
  const crop = pick(
    row?.orderOfferItemCrop,
    row?.crop,
    row?.salesOrderItemCrop,
    row?.purchaseOrderItemCrop,
  );
  const origin = pick(
    row?.orderOfferItemOrigin,
    row?.itemOrigin,
    row?.salesOrderItemOrigin,
    row?.purchaseOrderItemOrigin,
  );
  if (buying) parts.push(`Buying ${buying}`);
  if (crop) parts.push(`Crop ${crop}`);
  if (origin) parts.push(`Origin ${origin}`);
  return parts.join(" | ");
}
function poLabel(poId, row) {
  const company = pick(
    row?.poCompany,
    row?.shipmentRequestPurchaseOrderCompany,
    row?.purchaseOrderCompany,
    row?.orderCompany,
  );
  const stakeholder = pick(
    row?.shippingInstructionShipmentRequestPurchaseOrderOfferStakeholderName,
    row?.shipmentRequestPurchaseOrderOfferStakeholderName,
    row?.purchaseOrderOfferStakeholderName,
    row?.orderOfferStakeholderName,
  );
  const sap = row?.purchaseOrderNumber ? `SAP ${row.purchaseOrderNumber}` : null;
  const f = orderFlow(row);
  const qty =
    f?.volume != null && f?.volumeUnit
      ? `PO QTY ${nf(f.volume)} ${f.volumeUnit}`
      : f?.cases != null
      ? `PO QTY ${nfi(f.cases)} cases`
      : null;
  const head = [
    `PO ${poId}`,
    company ? `Company ${company}` : null,
    stakeholder ? `Stakeholder ${stakeholder}` : null,
    sap,
  ]
    .filter(Boolean)
    .join(" | ");
  return qty ? `${head}<br/>${qty}` : head;
}
function soLabel(soId, row) {
  const company = pick(
    row?.soCompany,
    row?.shipmentRequestSalesOrderCompany,
    row?.salesOrderCompany,
    row?.orderCompany,
  );
  const stakeholder = pick(
    row?.shippingInstructionShipmentRequestSalesOrderOfferStakeholderName,
    row?.shipmentRequestSalesOrderOfferStakeholderName,
    row?.salesOrderOfferStakeholderName,
    row?.orderOfferStakeholderName,
  );
  const sap = row?.salesOrderNumber ? `SAP ${row.salesOrderNumber}` : null;
  const f = orderFlow(row);
  const qty =
    f?.volume != null && f?.volumeUnit
      ? `SO QTY ${nf(f.volume)} ${f.volumeUnit}`
      : f?.cases != null
      ? `SO QTY ${nfi(f.cases)} cases`
      : null;
  const head = [
    `SO ${soId}`,
    company ? `Company ${company}` : null,
    stakeholder ? `Stakeholder ${stakeholder}` : null,
    sap,
  ]
    .filter(Boolean)
    .join(" | ");
  return qty ? `${head}<br/>${qty}` : head;
}
function srLabel(srId, row) {
  const tp = row?.shipmentRequestType ? `Type ${row.shipmentRequestType}` : null;
   const whs = pick(
    row?.shipmentRequestWarehouse,
    row?.warehouse,
    row?.warehouseName
  );
  const dest = pick(
    row?.shipmentRequestDestination,
    row?.destination,
    row?.destinationPort
  );
  const parts = [[`SR ${srId}`, tp].filter(Boolean).join(" | ")];
  if (whs) parts.push(`Whs ${whs}`);
  if (dest) parts.push(`Dest ${dest}`);

  return parts.join("<br/>");
}
function siLabel(siId, row) {
  const no = row?.finalShippingInstructionNumber
    ? `No ${row.finalShippingInstructionNumber}`
    : null;
  const ataRaw =
    row?.actualTimeOfArrival ??
    row?.shippingInstructionActualTimeOfArrival ??
    row?.shippingInstructionShipmentRequestActualTimeOfArrival ??
    row?.ata;
  const ata = ataRaw != null ? `ATA ${fmtDate(ataRaw)}` : null;
  const refId = row?.shipmentRequestReferenceShippingInstructionId;
  const refNo = row?.shipmentRequestReferenceShippingInstructionNumber;

  const head = [`SI ${siId}`, no, ata].filter(Boolean).join(" | ");
  
  const refs = [
    refNo ? `Ref No ${refNo}` : null,
    refId ? `Ref ID ${refId}` : null,
  ]
    .filter(Boolean)
    .join(" | ");

  return refs ? `${head}<br/>${refs}` : head;
}

// ---------- model ----------
function buildModel(rows = []) {
  const nodes = new Map();
  const edges = new Map();

  const addOrUpdateNode = (type, key, label) => {
    const ex = nodes.get(key);
    if (!ex) {
      nodes.set(key, { id: key, type, label });
      return;
    }
    // Prefer labels that include more info (e.g., ATA, No)
   if (label && label !== ex.label) {
      const score = (s) =>
        (s && s.includes("ATA") ? 1 : 0) +
        (s && s.includes("No ") ? 1 : 0) +
        (s && s.includes("Whs ") ? 1 : 0) +
        (s && s.includes("Dest ") ? 1 : 0) +
        (s && s.includes("Ref ") ? 1 : 0);
      const better = score(label) >= score(ex.label) ? label : ex.label;
      nodes.set(key, { ...ex, label: better });
    }
  };
  // accumulate across all rows; only show cases when we actually saw a cases value
  const addEdge = (from, to, delta, kind) => {
    if (!from || !to) return;

    const k = `${from}__${to}`;
    const ex = edges.get(k) || { from, to, kind, cases: null, volumes: {}, kg: 0 };

    if (delta?.cases != null) {
      ex.cases = (ex.cases ?? 0) + (Number(delta.cases) || 0);
    }

    // Accumulate original volumes per unit AND always accumulate total in KG
    if (delta?.volume != null) {
      const u = String(delta.volumeUnit || "KG").toUpperCase();
      const vol = Number(delta.volume) || 0;
      ex.volumes[u] = (ex.volumes[u] || 0) + vol;
      ex.kg = (ex.kg || 0) + (toKG(vol, u) || 0);
    }

    ex.kind = kind || ex.kind;
    edges.set(k, ex);
  };

  for (const row of rows) {
    if (isPassive(row)) continue;

    const { itemId, poId, soId, srId, siId } = buildIds(row);

    if (itemId) addOrUpdateNode("item", nodeKey("item", itemId), itemLabel(itemId, row));
    if (poId) addOrUpdateNode("po", nodeKey("po", poId), poLabel(poId, row));
    if (soId) addOrUpdateNode("so", nodeKey("so", soId), soLabel(soId, row));
    if (srId) addOrUpdateNode("sr", nodeKey("sr", srId), srLabel(srId, row));
    if (siId) addOrUpdateNode("si", nodeKey("si", siId), siLabel(siId, row));

    if (itemId && poId)
      addEdge(nodeKey("item", itemId), nodeKey("po", poId), orderFlow(row), "item-po");
    if (itemId && soId)
      addEdge(nodeKey("item", itemId), nodeKey("so", soId), orderFlow(row), "item-so");
    if (poId && srId)
      addEdge(nodeKey("po", poId), nodeKey("sr", srId), srFlow(row), "po-sr");
    if (soId && srId)
      addEdge(nodeKey("so", soId), nodeKey("sr", srId), srFlow(row), "so-sr");
    if (srId && siId)
      addEdge(nodeKey("sr", srId), nodeKey("si", siId), siFlow(row), "sr-si");
  }

  return { nodes, edges };
}

function computeTotals({ edges }) {
  let poKg = 0, soKg = 0, siKg = 0;
  for (const e of edges.values()) {
    const kg = Number(e.kg) || 0;
    if (e.kind === "item-po") poKg += kg;
    else if (e.kind === "item-so") soKg += kg;
    else if (e.kind === "sr-si") siKg += kg; // shipped
  }
  return { poKg, soKg, siKg };
}

// ---------- Mermaid builder ----------
function buildMermaidSimple({ nodes, edges }) {
  const lines = [];
  lines.push('%%{init: {"flowchart": {"curve": "linear", "htmlLabels": true}} }%%');
  lines.push("flowchart LR");

  // Node classes
  lines.push("classDef item fill:#E8F1FF,stroke:#1E88E5,stroke-width:2px,color:#0D47A1");
  lines.push("classDef po   fill:#E8FFE8,stroke:#43A047,stroke-width:2px,color:#1B5E20");
  lines.push("classDef so   fill:#FFF3E0,stroke:#FB8C00,stroke-width:2px,color:#E65100");
  lines.push("classDef sr   fill:#F3E5F5,stroke:#8E24AA,stroke-width:2px,color:#4A148C");
  lines.push("classDef si   fill:#EDE7F6,stroke:#5E35B1,stroke-width:2px,color:#311B92");

  const all = Array.from(nodes.values());
  all.sort((a, b) => {
    const t = a.type.localeCompare(b.type);
    return t !== 0 ? t : numPart(a.id) - numPart(b.id);
  });

  for (const n of all) {
    const id = sanitizeId(n.id);
    const shape = shapeWrap(n.type, n.label);
    lines.push(`  ${id}${shape}`);
    lines.push(`  class ${id} ${n.type}`);
  }

  // Edges with explicit prefixes; show cases and KG when available
  const edgeList = [];
  for (const e of edges.values()) {
    const from = sanitizeId(e.from);
    const to = sanitizeId(e.to);
    const kg = e.kg != null ? e.kg : null;
    const cases = e.cases != null ? `${nfi(e.cases)} cases` : "";
    const qty =
      cases && kg != null
        ? `${cases}, ${nf(kg)} KG`
        : cases
        ? cases
        : kg != null
        ? `${nf(kg)} KG`
        : "";
    const prefix =
      e.kind === "item-po"
        ? "PO QTY"
        : e.kind === "item-so"
        ? "SO QTY"
        : e.kind === "sr-si"
        ? "SI QTY"
        : "SR QTY";
    const label = sanitizeEdgeLabel(qty ? `${prefix} ${qty}` : prefix);
    edgeList.push({ from, to, label, kind: e.kind });
  }

  for (const e of edgeList) lines.push(`  ${e.from} -->|${e.label}| ${e.to}`);

  const styleFor = (k) => {
    if (k === "item-po" || k === "po-sr")
      return "stroke:#43A047,stroke-width:2px,color:#1B5E20";
    if (k === "item-so" || k === "so-sr")
      return "stroke:#FB8C00,stroke-width:2px,color:#E65100";
    if (k === "sr-si") return "stroke:#8E24AA,stroke-width:2px,color:#4A148C";
    return "stroke:#90A4AE,stroke-width:1.5px,color:#455A64";
  };
  edgeList.forEach((e, i) => lines.push(`linkStyle ${i} ${styleFor(e.kind)}`));

  return lines.join("\n");
}

// ---------- Mermaid loader ----------
function loadMermaid() {
  return new Promise((resolve, reject) => {
    const init = () => {
      try {
        window.mermaid.initialize({
          startOnLoad: false,
          securityLevel: "loose",
          theme: "default",
          themeCSS: ".mermaid { background: #ffffff; }",
          flowchart: { curve: "linear", htmlLabels: true, useMaxWidth: true, padding: 8 },
        });
      } catch {}
      resolve(window.mermaid);
    };
    if (window.mermaid) {
      init();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js";
    s.onload = init;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

// Wait until the dialog container is laid out (fixes first-open glitch)
async function waitForContainer(ref, timeoutMs = 1500) {
  const start = performance.now();
  while (true) {
    const el = ref.current;
    if (el && el.isConnected) {
      const w = el.clientWidth || el.getBoundingClientRect().width;
      if (w > 0) break;
    }
    await new Promise((r) => requestAnimationFrame(r));
    if (performance.now() - start > timeoutMs) break; // proceed anyway
  }
}

// ---------- SVG export helpers ----------
function sanitizeSvgForCanvas(svg) {
  let clean = svg;
  clean = clean.replace(/@import[^;]+;/g, "");
  clean = clean.replace(/url\((['"]?)(https?:)[^)]+\)/g, "none");
  clean = clean.replace(/\s(xlink:href|href)=(['"])(https?:)[^'"]+\2/gi, "");
  return clean;
}
function serializeVisibleSvg(svgEl, pad = 20, footerLines = null) {
  const bbox = svgEl.getBBox();
  const vbX = bbox.x - pad;
  const vbY = bbox.y - pad;
  const vbW = Math.max(1, bbox.width + pad * 2);
  const vbH = Math.max(1, bbox.height + pad * 2);

  const footerH = footerLines && footerLines.length ? 48 + (footerLines.length - 1) * 16 : 0;
  const totalH = vbH + footerH;

  const clone = svgEl.cloneNode(true);
  const NS = "http://www.w3.org/2000/svg";
  const bg = document.createElementNS(NS, "rect");
  bg.setAttribute("x", String(vbX));
  bg.setAttribute("y", String(vbY));
  bg.setAttribute("width", String(vbW));
  bg.setAttribute("height", String(totalH));
  bg.setAttribute("fill", "#ffffff");
  clone.insertBefore(bg, clone.firstChild);
  clone.setAttribute("viewBox", `${vbX} ${vbY} ${vbW} ${totalH}`);
  clone.setAttribute("width", String(vbW));
  clone.setAttribute("height", String(totalH));

    if (footerH) {
    // separator
    const sep = document.createElementNS(NS, "line");
    const sepY = vbY + vbH + 0.5;
    sep.setAttribute("x1", String(vbX));
    sep.setAttribute("y1", String(sepY));
    sep.setAttribute("x2", String(vbX + vbW));
    sep.setAttribute("y2", String(sepY));
    sep.setAttribute("stroke", "#e0e0e0");
    sep.setAttribute("stroke-width", "1");
    clone.appendChild(sep);

    // footer text
    const g = document.createElementNS(NS, "g");
    g.setAttribute("font-family", "Arial, Helvetica, sans-serif");
    g.setAttribute("font-size", "15");
    g.setAttribute("fill", "#111");
    let y = vbY + vbH + 18;
    const x = vbX + 8;

    footerLines.forEach((line, i) => {
      const t = document.createElementNS(NS, "text");
      t.setAttribute("x", String(x));
      t.setAttribute("y", String(y + i * 16));
      t.textContent = line;
      g.appendChild(t);
    });
    clone.appendChild(g);
  }

  const xml = new XMLSerializer().serializeToString(clone);
  return { xml, width: vbW, height: totalH };
}

// ---------- React dialog ----------
export default function TradeRelationsGraphDialog({ open, onClose, rows }) {
  const containerRef = React.useRef(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [hasSvg, setHasSvg] = React.useState(false);
  const [entered, setEntered] = React.useState(false);
  const [totals, setTotals] = React.useState({ poKg: 0, soKg: 0, siKg: 0 });

  React.useEffect(() => {
    if (!open) {
      setEntered(false);
      setHasSvg(false);
      setError("");
      setLoading(false);
    }
  }, [open]);

  React.useEffect(() => {
    let mounted = true;
    
    async function render() {
      // Must have open dialog AND completed transition
      if (!open || !entered) return;
      
      setLoading(true);
      setError("");
      setHasSvg(false);
      
      try {
        // Load mermaid first (can happen before container is ready)
        const mermaid = await loadMermaid();
        if (!mounted) return;

        // Now wait for container to be ready
        await waitForContainer(containerRef, 2000);
        if (!mounted) return;
        
        if (!containerRef.current) {
          throw new Error("Container not available");
        }

        const model = buildModel(rows || []);
        setTotals(computeTotals(model));
        const code = buildMermaidSimple(model);

        await mermaid.parse(code);
        if (!mounted) return;

        // Clear container before render
        containerRef.current.innerHTML = "";

        const id = "tradeGraph_" + Math.random().toString(36).slice(2);
        const { svg } = await mermaid.render(id, code);
        if (!mounted || !containerRef.current) return;

        containerRef.current.innerHTML = svg;
        const svgEl = containerRef.current.querySelector("svg");
        if (svgEl) {
          svgEl.style.maxWidth = "100%";
          svgEl.style.width = "1200px";
          svgEl.style.height = "auto";
        }
        setHasSvg(true);
      } catch (e) {
        console.error("Mermaid render error:", e);
        if (mounted) setError("Failed to render graph: " + e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    
    render();
    
    return () => {
      mounted = false;
    };
  }, [open, entered, rows]);

  const downloadSVG = () => {
    const svgEl = containerRef.current?.querySelector("svg");
    if (!svgEl) return;
    const footer = [
    "Totals",
    `PO total: ${nf(totals.poKg)} KG`,
    `SO total: ${nf(totals.soKg)} KG`,
    `Shipments total: ${nf(totals.siKg)} KG`,
  ];
    const { xml } = serializeVisibleSvg(svgEl, 20, footer);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "trade-relations.svg";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadPNG = (scale = 2) => {
    const svgEl = containerRef.current?.querySelector("svg");
    if (!svgEl) return;
      const footer = [
    "Totals",
    `PO total: ${nf(totals.poKg)} KG`,
    `SO total: ${nf(totals.soKg)} KG`,
    `Shipments total: ${nf(totals.siKg)} KG`,
  ];
    const snap = serializeVisibleSvg(svgEl, 20, footer);
    const cleaned = sanitizeSvgForCanvas(snap.xml);
    const svgUrl = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(cleaned);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(snap.width * scale));
      canvas.height = Math.max(1, Math.floor(snap.height * scale));
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((pngBlob) => {
        const dlUrl = URL.createObjectURL(pngBlob);
        const a = document.createElement("a");
        a.href = dlUrl;
        a.download = "trade-relations.png";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(dlUrl);
      }, "image/png");
    };
    img.onerror = () => {};
    img.src = svgUrl;
  };

  const hasGraph = hasSvg && !loading && !error;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      keepMounted={false}
      TransitionProps={{
        onEntered: () => setEntered(true),
        onExited: () => setEntered(false),
      }}
    >
      <DialogTitle>Trade Relations Graph</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 4,
              gap: 1,
            }}
          >
            <CircularProgress size={28} />
            <Box sx={{ fontSize: 12, color: "text.secondary" }}>
              Rendering graph, please wait...
            </Box>
          </Box>
        )}
        {!loading && error && <Box sx={{ color: "error.main" }}>{error}</Box>}
        {/* Always render container so ref is available; hide when loading or error */}
        <Box
          ref={containerRef}
          sx={{
            overflowX: "auto",
            display: loading || error ? "none" : "block",
          }}
        />
        {!loading && !error && hasSvg && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              fontSize: 13,
            }}
          >
            <Box sx={{ fontWeight: 600, mb: 0.5 }}>Totals</Box>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              <Box>PO total: {nf(totals.poKg)} KG</Box>
              <Box>SO total: {nf(totals.soKg)} KG</Box>
              <Box>Shipments total: {nf(totals.siKg)} KG</Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={downloadSVG} disabled={!hasGraph}>
          Download SVG
        </Button>
        <Button onClick={() => downloadPNG(2)} disabled={!hasGraph}>
          Download PNG
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}